/**
 * Superadmin — Tenants Management Page
 * List, filter, and manage all tenants
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  MagnifyingGlassIcon,
  EyeIcon,
  PencilSquareIcon,
  ArrowRightOnRectangleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const API = process.env.REACT_APP_API_URL || 'https://api.shunrindo.com/api/v1';

const STATUS_COLORS = {
  active: 'bg-green-100 text-green-800',
  trialing: 'bg-blue-100 text-blue-800',
  past_due: 'bg-yellow-100 text-yellow-800',
  suspended: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-800'
};

function getAuthHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('accessToken')}` };
}

export default function TenantsPage() {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [stats, setStats] = useState(null);

  const fetchTenants = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, limit: 20 };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;

      const [tenantsRes, statsRes] = await Promise.all([
        axios.get(`${API}/superadmin/tenants`, { headers: getAuthHeaders(), params }),
        axios.get(`${API}/superadmin/stats`, { headers: getAuthHeaders() })
      ]);

      setTenants(tenantsRes.data.tenants);
      setTotal(tenantsRes.data.total);
      setStats(statsRes.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load tenants');
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter]);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  const handleImpersonate = async (tenant) => {
    if (!window.confirm(`Switch to viewing as "${tenant.businessName}"? You will be redirected to their admin panel.`)) return;
    try {
      const res = await axios.post(
        `${API}/superadmin/impersonate/${tenant.id}`,
        {},
        { headers: getAuthHeaders() }
      );
      // Save superadmin tokens before switching
      localStorage.setItem('superadminToken', localStorage.getItem('accessToken'));
      localStorage.setItem('superadminRefreshToken', localStorage.getItem('refreshToken') || '');
      // Switch to impersonation token
      localStorage.setItem('accessToken', res.data.impersonationToken);
      localStorage.setItem('tenantId', String(tenant.id));
      localStorage.setItem('impersonationActive', 'true');
      localStorage.setItem('impersonatedTenantName', tenant.businessName);
      navigate('/admin');
      window.location.reload();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to impersonate tenant');
    }
  };

  const handleStatusChange = async (tenantId, newStatus) => {
    try {
      await axios.patch(
        `${API}/superadmin/tenants/${tenantId}`,
        { subscriptionStatus: newStatus },
        { headers: getAuthHeaders() }
      );
      fetchTenants();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update status');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <div className="space-y-6">
      {/* Stats cards */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Total Tenants', value: stats.tenants?.totalTenants, color: 'text-gray-900' },
            { label: 'Active', value: stats.tenants?.activeTenants, color: 'text-green-600' },
            { label: 'Trial', value: stats.tenants?.trialTenants, color: 'text-blue-600' },
            { label: 'Issues', value: stats.tenants?.problemTenants, color: 'text-red-600' }
          ].map(stat => (
            <div key={stat.label} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <p className="text-xs text-gray-500 mb-1">{stat.label}</p>
              <p className={`text-2xl font-bold ${stat.color}`}>{stat.value ?? '—'}</p>
            </div>
          ))}
        </div>
      )}

      {/* Revenue cards */}
      {stats?.revenue && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <p className="text-xs text-gray-500 mb-1">Total Revenue</p>
            <p className="text-2xl font-bold text-gray-900">
              €{Number(stats.revenue.totalRevenue || 0).toFixed(2)}
            </p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <p className="text-xs text-gray-500 mb-1">This Month</p>
            <p className="text-2xl font-bold text-green-600">
              €{Number(stats.revenue.revenueThisMonth || 0).toFixed(2)}
            </p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <p className="text-xs text-gray-500 mb-1">Pending Invoices</p>
            <p className="text-2xl font-bold text-amber-600">{stats.revenue.pendingInvoices ?? 0}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, email, subdomain..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
        </div>
        <select
          value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
        >
          <option value="">All statuses</option>
          <option value="trialing">Trialing</option>
          <option value="active">Active</option>
          <option value="past_due">Past Due</option>
          <option value="suspended">Suspended</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {error && (
          <div className="p-4 bg-red-50 border-b border-red-100 flex items-center gap-2 text-red-700 text-sm">
            <ExclamationTriangleIcon className="w-4 h-4" />
            {error}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">Tenant</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide hidden md:table-cell">Plan</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">Status</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide hidden lg:table-cell">Trial / Renewal</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide hidden xl:table-cell">Revenue</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-4 py-12 text-center text-gray-400">Loading...</td>
                </tr>
              ) : tenants.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-4 py-12 text-center text-gray-400">No tenants found</td>
                </tr>
              ) : tenants.map(tenant => (
                <tr key={tenant.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{tenant.businessName}</div>
                    <div className="text-xs text-gray-500">{tenant.subdomain}.shunrindo.com</div>
                    <div className="text-xs text-gray-400 hidden sm:block">{tenant.contactEmail}</div>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <span className="text-gray-700">{tenant.planName || 'Free'}</span>
                    {tenant.priceMonthly > 0 && (
                      <div className="text-xs text-gray-400">€{tenant.priceMonthly}/mo</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[tenant.subscriptionStatus] || 'bg-gray-100 text-gray-600'}`}>
                      {tenant.subscriptionStatus}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs hidden lg:table-cell">
                    {tenant.subscriptionStatus === 'trialing'
                      ? `Trial ends ${formatDate(tenant.trialEndsAt)}`
                      : formatDate(tenant.subscriptionEndsAt)}
                  </td>
                  <td className="px-4 py-3 text-gray-700 hidden xl:table-cell">
                    {tenant.totalRevenue ? `€${Number(tenant.totalRevenue).toFixed(2)}` : '€0.00'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => navigate(`/superadmin/tenants/${tenant.id}`)}
                        title="View details"
                        className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
                      >
                        <EyeIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleImpersonate(tenant)}
                        title="Impersonate tenant"
                        className="p-1.5 rounded-lg text-purple-500 hover:bg-purple-50 hover:text-purple-700 transition-colors"
                      >
                        <ArrowRightOnRectangleIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {total > 20 && (
          <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between text-sm text-gray-500">
            <span>Showing {Math.min((page - 1) * 20 + 1, total)}–{Math.min(page * 20, total)} of {total}</span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 border rounded-lg disabled:opacity-40 hover:bg-gray-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={page * 20 >= total}
                className="px-3 py-1 border rounded-lg disabled:opacity-40 hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
